import { useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { Button } from 'components/Button';
import { useStore } from 'context/store-context';
import { useUser } from 'context/user-context';
import { GENERATE_INSTALL_LINK } from 'gql/mutations';
import {
  OrderEditingSettings,
  OrderEditingWorkspace,
} from 'types/order-editing';
import { beginShopifyReauthorization, getShopifyAuthorizationLink } from 'utils/shopifyReauthorization';

const validInstallLink = (
  link: string,
  domain: string,
  missingScopes: string[],
) => {
  try {
    const url = new URL(getShopifyAuthorizationLink(link));
    if (url.hostname !== domain || url.pathname !== '/admin/oauth/authorize')
      return false;
    const scopes = new Set((url.searchParams.get('scope') || '').split(','));
    return missingScopes.every(
      (scope) =>
        scopes.has(scope) ||
        (scope.startsWith('read_') &&
          scopes.has(scope.replace('read_', 'write_'))),
    );
  } catch (_) {
    return false;
  }
};

export function ItemEditPermissionsNotice({
  workspace,
  draft,
  busy,
  dirty,
  onRefresh,
}: {
  workspace: OrderEditingWorkspace;
  draft: OrderEditingSettings;
  busy: boolean;
  dirty: boolean;
  onRefresh: () => void;
}) {
  const client = useApolloClient();
  const location = useLocation();
  const { storeId, storeDomain, storeProperties } = useStore();
  const { role, user } = useUser();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const alive = useRef(true);
  const mayRedirect = useRef(false);
  useEffect(
    () => () => {
      alive.current = false;
    },
    [],
  );
  const permissions = workspace.setup.itemEditPermissions;
  const usesItems = (
    ['quantity', 'removeItems', 'variantSwap', 'addItems'] as const
  ).some(
    (action) => draft.expanded?.[action] && draft.expanded[action] !== 'off',
  );
  const canUpdate = role === 'ADMIN' || user?.defaultRole === 'shipaid_admin';
  const storeReady =
    !!storeDomain &&
    storeId === workspace.storeId &&
    storeProperties?.id === storeId &&
    storeProperties.store === storeDomain;
  const disabled = busy || pending || dirty || !storeReady;
  const needsUpdate =
    !!usesItems &&
    permissions?.status === 'action_required' &&
    permissions.missingScopes.length > 0;
  mayRedirect.current = needsUpdate && canUpdate && storeReady && !dirty;
  if (!needsUpdate || !permissions) return null;

  const update = async () => {
    if (!canUpdate || disabled || inFlight.current || !storeDomain) return;
    inFlight.current = true;
    setPending(true);
    setError('');
    try {
      const link = (
        await client.mutate<{
          generateInstallLink: string;
        }>({
          mutation: GENERATE_INSTALL_LINK,
          variables: { store: storeDomain },
        })
      ).data?.generateInstallLink;
      if (!alive.current || !mayRedirect.current) return;
      if (
        !link ||
        !validInstallLink(link, storeDomain, permissions.missingScopes)
      )
        throw new Error('Invalid authorization link');
      beginShopifyReauthorization(
        link,
        `${location.pathname}${location.search}${location.hash}`,
      );
    } catch (_) {
      if (alive.current)
        setError(
          'Unable to start the permission update. Contact SHIPAID support or try again.',
        );
    } finally {
      inFlight.current = false;
      if (alive.current) setPending(false);
    }
  };

  return (
    <section
      className="rounded-lg border border-amber-200 bg-amber-50 p-5"
      aria-labelledby="item-permissions-heading"
    >
      <h2 id="item-permissions-heading" className="text-base font-semibold">
        Update permissions for item edits
      </h2>
      <p className="mt-1 text-sm text-gray-700">
        Allow SHIPAID to edit items, check inventory and locations, and read
        payment terms to confirm item-edit eligibility. Address editing and
        cancellation use their existing permissions.
      </p>
      <p className="mt-1 text-sm text-gray-600">
        Your saved settings and existing usage billing are kept. No new billing
        approval.
      </p>
      {!canUpdate ? (
        <p className="mt-3 text-sm">
          Ask a store admin to update Shopify permissions.
        </p>
      ) : (
        <>
          {dirty && (
            <p className="mt-3 text-sm">
              Save your changes before updating permissions.
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-3">
            <Button
              disabled={disabled}
              isLoading={pending}
              onClick={() => {
                void update();
              }}
            >
              Update Shopify permissions
            </Button>
            <Button
              variant="outlined"
              disabled={busy || pending}
              onClick={onRefresh}
            >
              Check permissions
            </Button>
          </div>
        </>
      )}
      {error && (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {error}
        </p>
      )}
    </section>
  );
}
